import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const DiaryModal = ({ booking, canCheckin, open, onClose }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!booking) return;
    api.get(`/bookings/${booking.id}/checkins`).then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    if (open) load();
  }, [open, booking?.id]);

  const checkin = async () => {
    setSaving(true);
    try {
      await api.post(`/bookings/${booking.id}/checkins`);
      toast.success(t("diary.registered"));
      load();
    } catch (e) {
      toast.error(formatApiError(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl" data-testid="diary-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">{t("diary.title")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-400">{t("diary.hint")}</p>
        <div className="max-h-64 overflow-y-auto space-y-2 py-2" data-testid="diary-entries">
          {items === null ? (
            <p className="text-sm text-slate-400">{t("common.loading")}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="diary-empty">{t("diary.empty")}</p>
          ) : (
            items.map((e) => (
              <div key={e.id} className="flex items-center gap-2.5 bg-emerald-50 rounded-xl px-4 py-2.5" data-testid={`diary-entry-${e.id}`}>
                <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
                <span className="text-sm text-emerald-800 font-medium">{t("diary.okLabel")}</span>
                <span className="text-xs text-slate-400 ml-auto">
                  {new Date(e.created_at).toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))
          )}
        </div>
        {canCheckin && (
          <Button data-testid="diary-checkin-button" onClick={checkin} disabled={saving} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold">
            {t("diary.checkin")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
